import { useState } from "react";
import {
  ALL,
  GENRE_OPTIONS,
  REGION_OPTIONS,
  SORT_OPTIONS,
  YEAR_OPTIONS,
  type FilterState,
  type SortKey,
} from "@/utils/catalog";

interface Props {
  filter: FilterState;
  sort: SortKey;
  onFilterChange: (next: FilterState) => void;
  onSortChange: (next: SortKey) => void;
  onReset: () => void;
  defaultOpen?: boolean;
}

interface ChipRowProps {
  label: string;
  options: string[];
  value: string;
  onSelect: (next: string) => void;
}

function ChipRow({ label, options, value, onSelect }: ChipRowProps) {
  return (
    <div className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-start sm:gap-4">
      <span className="shrink-0 pt-1 text-xs font-medium text-foreground-500 sm:w-12 sm:text-[13px]">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const active = option === value;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onSelect(option)}
              className={`cursor-pointer whitespace-nowrap rounded px-2.5 py-1 text-xs transition-colors ${
                active
                  ? "bg-primary-600 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-primary-700"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({
  filter,
  sort,
  onFilterChange,
  onSortChange,
  onReset,
  defaultOpen = false,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);

  const activeCount = [filter.region, filter.year, filter.genre].filter((item) => item !== ALL).length;

  const update = (patch: Partial<FilterState>) => onFilterChange({ ...filter, ...patch });

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between gap-3 border-b border-background-200 px-3 py-2.5 md:px-4">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-filter-3-line text-sm text-primary-600" aria-hidden="true"></i>
          </span>
          <span className="text-xs font-medium text-foreground-800 md:text-sm">筛选条件</span>
          {activeCount > 0 ? (
            <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[11px] font-medium text-accent-800">
              已选 {activeCount} 项
            </span>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={onReset}
            className="cursor-pointer whitespace-nowrap rounded px-2 py-1 text-xs text-foreground-500 transition-colors hover:text-primary-700"
          >
            重置
          </button>
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded border border-background-300 px-2 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 md:hidden"
          >
            {open ? "收起" : "展开"}
            <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`} aria-hidden="true"></i>
          </button>
          <div className="hidden items-center gap-1 md:flex">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => onSortChange(option.key)}
                className={`cursor-pointer whitespace-nowrap rounded px-2.5 py-1 text-xs transition-colors ${
                  sort === option.key
                    ? "bg-accent-500 font-medium text-primary-950"
                    : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={`${open ? "block" : "hidden"} divide-y divide-background-200 px-3 md:block md:px-4`}>
        <ChipRow
          label="地区"
          options={REGION_OPTIONS}
          value={filter.region}
          onSelect={(region) => update({ region })}
        />
        <ChipRow
          label="年份"
          options={YEAR_OPTIONS}
          value={filter.year}
          onSelect={(year) => update({ year })}
        />
        <ChipRow
          label="题材"
          options={GENRE_OPTIONS}
          value={filter.genre}
          onSelect={(genre) => update({ genre })}
        />
        <div className="flex flex-wrap items-center gap-2 py-2.5 md:hidden">
          <span className="shrink-0 text-xs font-medium text-foreground-500">排序</span>
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onSortChange(option.key)}
              className={`cursor-pointer whitespace-nowrap rounded px-2.5 py-1 text-xs transition-colors ${
                sort === option.key
                  ? "bg-accent-500 font-medium text-primary-950"
                  : "bg-background-100 text-foreground-600"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}